/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import type { Product } from '@shared/types/products';
import type { Issue } from '@shared/types/issues';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useToast } from '@/hooks/use-toast';
import {
  ClipboardCheck,
  Loader2,
  Play,
  FileText,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Code,
  Bug,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  TestTube2,
} from 'lucide-react';
import type {
  GeneratedFeatureArea,
  TestFailure,
  TestRunSummary,
} from '../../../services/skillshop';

export type TestingPlanScope = 'product' | 'release';

export interface TestingPlanResult {
  success: boolean;
  filePath?: string;
  fileName?: string;
  error?: string;
}

export interface TestingTabProps {
  product: Product;
  releases: Issue[];
  isGenerating: boolean;
  lastGeneratedPlan: TestingPlanResult | null;
  onGenerateTestingPlan: (scope: TestingPlanScope, releaseId?: string) => Promise<void>;
  onRefreshReleases: () => void;
  onGeneratePlaywrightTests?: () => Promise<void>;
  onRunTests?: (featureArea?: string) => Promise<void>;
  onCreateBugs?: (failures: TestFailure[]) => Promise<void>;
  isGeneratingTests?: boolean;
  isRunningTests?: boolean;
  isCreatingBugs?: boolean;
  generatedFeatureAreas?: GeneratedFeatureArea[];
  testResults?: {
    summary?: TestRunSummary;
    failures?: TestFailure[];
    reportPath?: string;
  } | null;
  onRefreshFeatureAreas?: () => void;
  onRefreshTestResults?: () => void;
}

export function TestingTab({
  product,
  releases,
  isGenerating,
  lastGeneratedPlan,
  onGenerateTestingPlan,
  onRefreshReleases,
  onGeneratePlaywrightTests,
  onRunTests,
  onCreateBugs,
  isGeneratingTests = false,
  isRunningTests = false,
  isCreatingBugs = false,
  generatedFeatureAreas = [],
  testResults = null,
  onRefreshFeatureAreas,
  onRefreshTestResults,
}: TestingTabProps) {
  const { toast } = useToast();
  const [scope, setScope] = useState<TestingPlanScope>('product');
  const [selectedReleaseId, setSelectedReleaseId] = useState<string>('');
  const [selectedFeatureArea, setSelectedFeatureArea] = useState<string>('all');
  const [selectedFailures, setSelectedFailures] = useState<Set<string>>(new Set());
  const [expandedFailures, setExpandedFailures] = useState<Set<string>>(new Set());

  useEffect(() => {
    onRefreshReleases();
    onRefreshFeatureAreas?.();
  }, [product.id]);

  useEffect(() => {
    if (scope === 'release' && releases.length > 0 && !selectedReleaseId) {
      setSelectedReleaseId(releases[0].id);
    }
  }, [scope, releases, selectedReleaseId]);

  const handleGeneratePlan = async () => {
    if (scope === 'release' && !selectedReleaseId) {
      toast({
        title: 'Select a release',
        description: 'Please select a release to generate a testing plan.',
        variant: 'destructive',
      });
      return;
    }

    await onGenerateTestingPlan(scope, scope === 'release' ? selectedReleaseId : undefined);
  };

  const handleGenerateTests = async () => {
    if (!onGeneratePlaywrightTests) return;
    await onGeneratePlaywrightTests();
    onRefreshFeatureAreas?.();
  };

  const handleRunTests = async () => {
    if (!onRunTests) return;
    const featureArea = selectedFeatureArea === 'all' ? undefined : selectedFeatureArea;
    await onRunTests(featureArea);
    onRefreshTestResults?.();
  };

  const handleCreateBugs = async () => {
    if (!onCreateBugs || !testResults?.failures) return;
    
    const failuresToFile = testResults.failures.filter(f => 
      selectedFailures.has(`${f.featureArea}-${f.testName}`)
    );
    
    if (failuresToFile.length === 0) {
      toast({
        title: 'No failures selected',
        description: 'Please select at least one failure to create a bug issue.',
        variant: 'destructive',
      });
      return;
    }

    await onCreateBugs(failuresToFile);
    setSelectedFailures(new Set());
  };

  const toggleFailureSelection = (failure: TestFailure) => {
    const key = `${failure.featureArea}-${failure.testName}`;
    const newSelected = new Set(selectedFailures);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedFailures(newSelected);
  };

  const toggleFailureExpanded = (failure: TestFailure) => {
    const key = `${failure.featureArea}-${failure.testName}`;
    const newExpanded = new Set(expandedFailures);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedFailures(newExpanded);
  };

  const selectAllFailures = () => {
    if (!testResults?.failures) return;
    const allKeys = testResults.failures.map(f => `${f.featureArea}-${f.testName}`);
    setSelectedFailures(new Set(allKeys));
  };

  const deselectAllFailures = () => {
    setSelectedFailures(new Set());
  };

  const selectedRelease = releases.find(r => r.id === selectedReleaseId);
  const hasTestingPlan = lastGeneratedPlan?.success;
  const hasGeneratedTests = generatedFeatureAreas.length > 0;

  return (
    <div className="space-y-6">
      {/* Testing Plan Generator */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            <CardTitle>Testing Plan Generator</CardTitle>
          </div>
          <CardDescription>
            Generate comprehensive testing plans based on user story acceptance criteria
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-3 block">Testing Plan Scope</Label>
                <RadioGroup
                  value={scope}
                  onValueChange={(value: string) => setScope(value as TestingPlanScope)}
                  className="flex flex-col gap-3"
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="product" id="scope-product" />
                    <Label htmlFor="scope-product" className="cursor-pointer">
                      <div className="font-medium">Full Product</div>
                      <div className="text-sm text-muted-foreground">
                        Generate a testing plan covering all user stories in the product
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="release" id="scope-release" />
                    <Label htmlFor="scope-release" className="cursor-pointer">
                      <div className="font-medium">Release-Scoped</div>
                      <div className="text-sm text-muted-foreground">
                        Generate a testing plan for user stories included in a specific release
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {scope === 'release' && (
                <div className="mt-4">
                  <Label className="text-sm font-medium mb-2 block">Select Release</Label>
                  {releases.length > 0 ? (
                    <Select
                      value={selectedReleaseId}
                      onValueChange={setSelectedReleaseId}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a release" />
                      </SelectTrigger>
                      <SelectContent>
                        {releases.map((release) => (
                          <SelectItem key={release.id} value={release.id}>
                            <div className="flex items-center gap-2">
                              <span>{release.id}: {release.title}</span>
                              {release.version && (
                                <Badge variant="outline" className="text-xs">
                                  {release.version}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 border rounded-md bg-muted/50">
                      <AlertCircle className="h-4 w-4" />
                      <span>No releases found. Create a release first to use release-scoped testing plans.</span>
                    </div>
                  )}
                  
                  {selectedRelease && (
                    <div className="mt-3 p-3 border rounded-md bg-background">
                      <div className="text-sm">
                        <span className="font-medium">{selectedRelease.title}</span>
                        {selectedRelease.version && (
                          <span className="text-muted-foreground ml-2">v{selectedRelease.version}</span>
                        )}
                      </div>
                      {selectedRelease.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {selectedRelease.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleGeneratePlan}
              disabled={isGenerating || !product.repositoryUrl || (scope === 'release' && !selectedReleaseId)}
              className="gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Generate Testing Plan
                </>
              )}
            </Button>
            
            {!product.repositoryUrl && (
              <p className="text-xs text-destructive">
                A repository URL is required to generate a testing plan.
              </p>
            )}
          </div>

          {lastGeneratedPlan && (
            <div className={`mt-4 p-4 rounded-lg border ${lastGeneratedPlan.success ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'}`}>
              {lastGeneratedPlan.success ? (
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100">Testing Plan Generated</h4>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      The testing plan has been saved to:
                    </p>
                    <code className="text-sm bg-green-100 dark:bg-green-900/50 px-2 py-1 rounded mt-2 inline-block">
                      {lastGeneratedPlan.fileName}
                    </code>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-red-900 dark:text-red-100">Generation Failed</h4>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                      {lastGeneratedPlan.error || 'An error occurred while generating the testing plan.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Automated Test Generation */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            <CardTitle>Automated Test Generation</CardTitle>
          </div>
          <CardDescription>
            Generate Playwright test files from your testing plan
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={handleGenerateTests}
              disabled={isGeneratingTests || !hasTestingPlan || !onGeneratePlaywrightTests}
              className="gap-2"
            >
              {isGeneratingTests ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating Tests...
                </>
              ) : (
                <>
                  <Code className="h-4 w-4" />
                  Generate Playwright Tests
                </>
              )}
            </Button>
            
            {onRefreshFeatureAreas && (
              <Button
                variant="outline"
                size="icon"
                onClick={onRefreshFeatureAreas}
                title="Refresh feature areas"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}

            {!hasTestingPlan && (
              <p className="text-xs text-muted-foreground">
                Generate a testing plan first.
              </p>
            )}
          </div>

          {hasGeneratedTests && (
            <div className="mt-4">
              <Label className="text-sm font-medium mb-2 block">Generated Feature Areas</Label>
              <div className="border rounded-lg divide-y">
                {generatedFeatureAreas.map((fa) => (
                  <div key={fa.id} className="p-3 flex items-center justify-between">
                    <div>
                      <span className="font-medium">{fa.id}</span>
                      <span className="text-muted-foreground ml-2">({fa.name})</span>
                    </div>
                    <Badge variant="secondary">{fa.testCount} tests</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Execution */}
      {hasGeneratedTests && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <TestTube2 className="h-5 w-5" />
              <CardTitle>Test Execution</CardTitle>
            </div>
            <CardDescription>
              Run automated Playwright tests and view results
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Select
                value={selectedFeatureArea}
                onValueChange={setSelectedFeatureArea}
              >
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select feature area" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Feature Areas</SelectItem>
                  {generatedFeatureAreas.map((fa) => (
                    <SelectItem key={fa.id} value={fa.id}>
                      {fa.id} ({fa.testCount} tests)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                onClick={handleRunTests}
                disabled={isRunningTests || !onRunTests}
                className="gap-2"
              >
                {isRunningTests ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Run Tests
                  </>
                )}
              </Button>
            </div>

            {/* Test Results */}
            {testResults && (
              <div className="mt-4 space-y-4">
                {/* Summary */}
                {testResults.summary && (
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 border rounded-lg text-center">
                      <div className="text-2xl font-bold">{testResults.summary.total}</div>
                      <div className="text-sm text-muted-foreground">Total</div>
                    </div>
                    <div className="p-3 border rounded-lg text-center bg-green-50 dark:bg-green-950/20">
                      <div className="text-2xl font-bold text-green-600">{testResults.summary.passed}</div>
                      <div className="text-sm text-green-600">Passed</div>
                    </div>
                    <div className="p-3 border rounded-lg text-center bg-red-50 dark:bg-red-950/20">
                      <div className="text-2xl font-bold text-red-600">{testResults.summary.failed}</div>
                      <div className="text-sm text-red-600">Failed</div>
                    </div>
                    <div className="p-3 border rounded-lg text-center bg-yellow-50 dark:bg-yellow-950/20">
                      <div className="text-2xl font-bold text-yellow-600">{testResults.summary.skipped}</div>
                      <div className="text-sm text-yellow-600">Skipped</div>
                    </div>
                  </div>
                )}

                {/* HTML Report Link */}
                {testResults.reportPath && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">HTML Report:</span>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {testResults.reportPath}
                    </code>
                  </div>
                )}

                {/* Failures */}
                {testResults.failures && testResults.failures.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Test Failures</Label>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={selectAllFailures}>
                          Select All
                        </Button>
                        <Button variant="ghost" size="sm" onClick={deselectAllFailures}>
                          Deselect All
                        </Button>
                      </div>
                    </div>

                    <div className="border rounded-lg divide-y">
                      {testResults.failures.map((failure) => {
                        const key = `${failure.featureArea}-${failure.testName}`;
                        const isSelected = selectedFailures.has(key);
                        const isExpanded = expandedFailures.has(key);

                        return (
                          <div key={key} className="p-3">
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleFailureSelection(failure)}
                              />
                              <Collapsible open={isExpanded} onOpenChange={() => toggleFailureExpanded(failure)} className="flex-1">
                                <div className="flex items-center justify-between">
                                  <CollapsibleTrigger className="flex items-center gap-2 text-left hover:text-primary">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                    <span className="font-medium">{failure.testName}</span>
                                  </CollapsibleTrigger>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline">{failure.featureArea}</Badge>
                                    {failure.userStoryId && (
                                      <Badge variant="secondary">{failure.userStoryId}</Badge>
                                    )}
                                  </div>
                                </div>
                                <CollapsibleContent className="mt-2">
                                  <pre className="text-sm bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap">
                                    {failure.error}
                                  </pre>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleCreateBugs}
                        disabled={isCreatingBugs || selectedFailures.size === 0 || !onCreateBugs}
                        variant="destructive"
                        className="gap-2"
                      >
                        {isCreatingBugs ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Creating Bugs...
                          </>
                        ) : (
                          <>
                            <Bug className="h-4 w-4" />
                            Create Bug Issues ({selectedFailures.size})
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {testResults.failures?.length === 0 && (testResults.summary?.passed ?? 0) > 0 && (
                  <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <div>
                      <h4 className="font-medium text-green-900 dark:text-green-100">All Tests Passed!</h4>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        All {testResults.summary?.passed ?? 0} tests completed successfully.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* About Testing */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            <CardTitle>About Testing</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            The testing workflow consists of three steps:
          </p>
          <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-2">
            <li><strong>Generate Testing Plan</strong> - Creates a comprehensive testing plan from user story acceptance criteria</li>
            <li><strong>Generate Playwright Tests</strong> - Converts the testing plan into executable Playwright test files</li>
            <li><strong>Run Tests</strong> - Executes the automated tests and captures results</li>
          </ol>
          
          <Separator />
          
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Output Locations</h4>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li><code className="bg-muted px-1 py-0.5 rounded">data/product/testing_plan.md</code> - Testing plan</li>
              <li><code className="bg-muted px-1 py-0.5 rounded">e2e/generated/</code> - Generated Playwright tests</li>
              <li><code className="bg-muted px-1 py-0.5 rounded">playwright-report/</code> - HTML test report</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
